import React, { useState, ChangeEvent, useEffect, useRef, Dispatch } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    TextField,
    Grid,
    Typography,
    Autocomplete,
} from '@mui/material';
import { ReportTypeMap } from './types';
import { FmtpError, FmtpErrorCode, ReportType, Report } from '@lanterds/types';
import { SnackbarKey, useSnackbar } from 'notistack';
import { editReport } from 'src/api/reports';

export type ModalReportForm = Omit<Report, 'powerBiDatasets' | 'reportType'> & {
    powerBiDatasetId: string;
    reportType: ReportType | null;
};

interface CreateReportDialogProps {
    open: boolean;
    onClose: () => void;
    onReportCreated: () => void;
    reportInfo?: Report | null;
    isEditing: boolean;
    setIsSavingReport: Dispatch<React.SetStateAction<boolean>>;
    reportTypeMapObj: ReportTypeMap;
}

const blankReportForm: ModalReportForm = {
    reportName: '',
    reportDisplayName: '',
    reportGroup: '',
    powerBiReportName: '',
    powerBiGroupId: '',
    powerBiDatasetId: '',
    sigmaPath: '',
    sigmaReportName: '',
    reportCatalog: '',
    fmtpRoute: '',
    reportType: null,
};

export default function EditReportDialog({
    open,
    onClose,
    onReportCreated,
    reportInfo,
    isEditing,
    setIsSavingReport,
    reportTypeMapObj,
}: CreateReportDialogProps) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [reportForm, setReportForm] = useState<ModalReportForm>(blankReportForm);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [reportExistsMessage, setReportExistsMessage] = useState('');
    const [isCreatingReport, setIsCreatingReport] = useState(false);
    const snackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        if (reportInfo) {
            setReportForm({
                reportName: reportInfo.reportName,
                reportDisplayName: reportInfo.reportDisplayName,
                reportGroup: reportInfo.reportGroup,
                powerBiReportName: reportInfo.powerBiReportName,
                powerBiGroupId: reportInfo.powerBiGroupId,
                powerBiDatasetId:
                    reportInfo?.powerBiDatasets && reportInfo?.powerBiDatasets[0] && reportInfo?.powerBiDatasets[0].datasetId
                        ? reportInfo?.powerBiDatasets[0].datasetId
                        : '',
                sigmaPath: reportInfo.sigmaPath,
                sigmaReportName: reportInfo.sigmaReportName,
                reportCatalog: reportInfo.reportCatalog,
                fmtpRoute: reportInfo.fmtpRoute,
                reportType: reportInfo.reportType,
            });
        }
        setFormErrors({});
        setReportExistsMessage('');
    }, [open, reportInfo]);

    const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setReportForm({ ...reportForm, [name]: value });
    };

    const onChangeReportType = (value: ReportType | null) => {
        setReportForm({ ...reportForm, reportType: value });
    };

    const validateFields = (): Partial<Report> => {
        const tempFormErrors: Record<string, string> = {};

        if (!reportForm.reportName) {
            tempFormErrors.reportName = 'FMTP ID cannot be blank.';
        }

        if (!reportForm.reportDisplayName) {
            tempFormErrors.reportDisplayName = 'Display Name cannot be blank.';
        }

        if (!reportForm.reportGroup) {
            tempFormErrors.reportGroup = 'Report Group cannot be blank.';
        }

        if (!reportForm.reportType) {
            tempFormErrors.reportType = 'Report Type cannot be blank.';
        }

        if (
            reportForm.reportType &&
            (reportForm.reportType === ReportType.PowerBi || reportForm.reportType === ReportType.PowerBiPaginated) &&
            !reportForm.powerBiReportName
        ) {
            tempFormErrors.powerBiReportName = 'PowerBI Report Name cannot be blank.';
        }

        if (
            reportForm.reportType &&
            (reportForm.reportType === ReportType.PowerBi || reportForm.reportType === ReportType.PowerBiPaginated) &&
            !reportForm.powerBiGroupId
        ) {
            tempFormErrors.powerBiGroupId = 'PowerBI Group ID cannot be blank.';
        }

        if (reportForm.reportType && reportForm.reportType === ReportType.PowerBiPaginated && !reportForm.powerBiDatasetId) {
            tempFormErrors.powerBiDatasets = 'PowerBI Dataset ID cannot be blank.';
        }

        if (reportForm.reportType && reportForm.reportType === ReportType.Sigma && !reportForm.sigmaPath) {
            tempFormErrors.sigmaPath = 'Sigma Path cannot be blank.';
        }

        if (reportForm.reportType && reportForm.reportType === ReportType.Sigma && !reportForm.sigmaReportName) {
            tempFormErrors.sigmaReportName = 'Sigma Report Name cannot be blank.';
        }

        if (reportForm.reportType && reportForm.reportType === ReportType.Sigma && !reportForm.reportCatalog) {
            tempFormErrors.sigmaReportName = 'Sigma Report Catalog cannot be blank.';
        }

        if (reportForm.reportType && reportForm.reportType === ReportType.Fmtp && !reportForm.fmtpRoute) {
            tempFormErrors.fmtpRoute = 'FMTP Webpage cannot be blank.';
        }

        setReportExistsMessage('');
        setFormErrors(tempFormErrors);
        return tempFormErrors;
    };

    const handleSave = async () => {
        const errors = validateFields();

        if (!Object.keys(errors).length) {
            setIsSavingReport(true);
            setIsCreatingReport(true);

            snackbarRef.current = enqueueSnackbar(`${!isEditing ? 'Creating New' : 'Editing'} Report...`, {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            try {
                const result = await editReport(reportForm, !isEditing);

                closeSnackbar(snackbarRef.current);

                if (result.error) {
                    snackbarRef.current = enqueueSnackbar(
                        `Report had error and not ${!isEditing ? 'Created' : 'Edited'}: ${result.error.message}`,
                        {
                            variant: 'error',
                            preventDuplicate: true,
                            disableWindowBlurListener: true,
                            action: (key) => (
                                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                                    Dismiss
                                </Button>
                            ),
                        },
                    );
                } else {
                    if (result.statusCode === 409) {
                        setReportExistsMessage(result.message);
                        closeSnackbar(snackbarRef.current);
                        setIsCreatingReport(false);
                        return;
                    }

                    snackbarRef.current = enqueueSnackbar(`Report Successfully ${!isEditing ? 'Created' : 'Edited'}!`, {
                        variant: 'success',
                        preventDuplicate: true,
                        disableWindowBlurListener: true,
                        action: (key) => (
                            <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                                Dismiss
                            </Button>
                        ),
                    });
                }

                onReportCreated();
                setIsCreatingReport(false);
                setReportForm({ ...blankReportForm });
                onClose();
            } catch (err) {
                const message = `An error occurred while ${!isEditing ? 'creating' : 'editing'} the report.`;
                const errorCode: FmtpErrorCode = 'SHU022';
                throw new FmtpError({
                    message,
                    errorCode,
                });
            }
        }
    };

    const handleCancel = () => {
        onClose();
        setReportForm({ ...blankReportForm });
    };

    return (
        <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
            <DialogTitle>{!isEditing ? 'Create New' : 'Edit'} Report</DialogTitle>
            <DialogContent>
                {/* TODO: remove sx and use class */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.reportName}
                                helperText={formErrors.reportName}
                                required
                                id="reportName"
                                name="reportName"
                                label="FMTP ID"
                                placeholder="FMTP ID (e.g. lanterReport)"
                                variant="outlined"
                                value={reportForm.reportName}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.reportDisplayName}
                                helperText={formErrors.reportDisplayName}
                                required
                                id="reportDisplayName"
                                name="reportDisplayName"
                                label="Display Name"
                                variant="outlined"
                                value={reportForm.reportDisplayName}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.reportGroup}
                                helperText={formErrors.reportGroup}
                                id="reportGroup"
                                name="reportGroup"
                                label="Display Group"
                                variant="outlined"
                                value={reportForm.reportGroup}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                id="reportType"
                                options={Object.values(ReportType)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        name="reportType"
                                        label="Report Type"
                                        required
                                        error={!!formErrors.reportType}
                                        helperText={formErrors.reportType}
                                        InputLabelProps={{ ...params.InputLabelProps }}
                                    />
                                )}
                                value={reportForm.reportType}
                                onChange={(_event, value) => onChangeReportType(value)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm &&
                            reportForm.reportType &&
                            (reportForm.reportType === ReportType.PowerBi ||
                                reportForm.reportType === ReportType.PowerBiPaginated)
                                ? 'block'
                                : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.powerBiReportName}
                                helperText={formErrors.powerBiReportName}
                                required
                                id="powerBiReportName"
                                name="powerBiReportName"
                                label="PowerBI Report Name"
                                variant="outlined"
                                value={reportForm.powerBiReportName}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm &&
                            reportForm.reportType &&
                            (reportForm.reportType === ReportType.PowerBi ||
                                reportForm.reportType === ReportType.PowerBiPaginated)
                                ? 'block'
                                : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.powerBiGroupId}
                                helperText={formErrors.powerBiGroupId}
                                required
                                id="powerBiGroupId"
                                name="powerBiGroupId"
                                label="PowerBI Group ID"
                                variant="outlined"
                                value={reportForm.powerBiGroupId}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm && reportForm.reportType && reportForm.reportType === ReportType.PowerBiPaginated
                                ? 'block'
                                : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.powerBiDatasets}
                                helperText={formErrors.powerBiDatasets}
                                required
                                id="powerBiDatasetId"
                                name="powerBiDatasetId"
                                label="PowerBI Dataset ID"
                                variant="outlined"
                                value={reportForm.powerBiDatasetId}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm && reportForm.reportType && reportForm.reportType === ReportType.Sigma ? 'block' : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.sigmaPath}
                                helperText={formErrors.sigmaPath}
                                required
                                id="sigmaPath"
                                name="sigmaPath"
                                label="Sigma Path"
                                variant="outlined"
                                value={reportForm.sigmaPath}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm && reportForm.reportType && reportForm.reportType === ReportType.Sigma ? 'block' : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.sigmaReportName}
                                helperText={formErrors.sigmaReportName}
                                required
                                id="sigmaReportName"
                                name="sigmaReportName"
                                label="Sigma Report Name"
                                variant="outlined"
                                value={reportForm.sigmaReportName}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm && reportForm.reportType && reportForm.reportType === ReportType.Sigma ? 'block' : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.sigmaReportName}
                                helperText={formErrors.sigmaReportName}
                                required
                                id="reportCatalog"
                                name="reportCatalog"
                                label="Sigma Report Catalog"
                                variant="outlined"
                                value={reportForm.reportCatalog}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        display={
                            reportForm && reportForm.reportType && reportForm.reportType === ReportType.Fmtp ? 'block' : 'none'
                        }
                    >
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.fmtpRoute}
                                helperText={formErrors.fmtpRoute}
                                required
                                id="fmtpRoute"
                                name="fmtpRoute"
                                label="FMTP Webpage"
                                variant="outlined"
                                value={reportForm.fmtpRoute}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    {reportExistsMessage && (
                        <Grid item xs={12}>
                            <Typography color="error">{reportExistsMessage}</Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isCreatingReport}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
